<!-- CoffeeLabel — everything printed on the bag, and the roaster's own words.
     (The bag itself and its product photos are BagHero, above this.)

     The shared body of the Label sheet (over the wall) and the Coffee page. It
     reads top to bottom the way you read a real bag: who roasted it, what it
     is, what it tastes like, then the fine print. Empty fields are left out,
     never shown as "—": a label with six dashes reads as broken data, not as a
     coffee. -->
<template>
  <article class="label" :aria-labelledby="showName ? nameId : undefined">
    <a
      class="roaster"
      :href="roasterPath"
      @click.prevent="goRoaster"
    >
      <span class="roaster__dot" :style="{ '--dot': colour }" aria-hidden="true" />
      <span>{{ coffee.roaster }}</span>
      <IonIcon :icon="chevronForward" aria-hidden="true" class="roaster__chev" />
    </a>

    <h2 v-if="showName" :id="nameId" class="name">{{ displayName(coffee.name) }}</h2>

    <div class="tags">
      <span v-if="flavour" class="chip" :style="{ '--fl': flavour.colour }">
        <IonIcon :icon="flavourIcon" aria-hidden="true" class="chip__icon" />
        <span>{{ flavour.name }}</span>
      </span>
      <span v-if="coffee.kind && coffee.kind !== 'Coffee'" class="chip chip--plain">{{ coffee.kind }}</span>
    </div>

    <section v-if="notes.length" class="notes" :aria-labelledby="notesId">
      <h3 :id="notesId" class="eyebrow">Tastes like</h3>
      <ul class="notes__list">
        <li v-for="n in notes" :key="n">{{ n }}</li>
      </ul>
    </section>

    <dl v-if="specs.length" class="specs">
      <div v-for="s in specs" :key="s.label" class="spec" :class="{ 'spec--wide': s.wide }">
        <dt><SpecGlossary :label="s.label" :value="s.scan" /></dt>
        <dd>{{ s.value }}</dd>
      </div>
    </dl>

    <!-- The size and quantity sit here, right under the facts, so the price on
         the button is never two screens away from the choice it depends on. -->
    <slot name="buy" />

    <section v-if="story.length" class="story" :aria-labelledby="storyId">
      <h3 :id="storyId" class="section-title story__title">The roaster says</h3>
      <div :id="storyBodyId" class="story__body">
        <template v-for="(p, i) in shownStory" :key="i">
          <p v-if="p.key" class="story__fact"><span class="story__key">{{ p.key }}</span> {{ p.text }}</p>
          <p v-else :class="{ story__notice: p.notice }">{{ p.text }}</p>
        </template>
      </div>
      <button
        v-if="story.length > 3"
        type="button"
        class="textlink"
        :aria-expanded="expanded ? 'true' : 'false'"
        :aria-controls="storyBodyId"
        @click="expanded = !expanded"
      >
        {{ expanded ? 'Show less' : 'Read more' }}
      </button>
    </section>

    <a class="textlink textlink--out" :href="coffee.url" target="_blank" rel="noopener noreferrer" @click.prevent="openExternal(coffee.url)">
      View on beanhub.com.au
      <IonIcon :icon="openOutline" aria-hidden="true" />
    </a>

  </article>
</template>

<script>
import { isNative, plugin } from '../core/native.js';

/** Open a page on beanhub.com.au: the in-app browser on a phone, a new tab on the web. */
export async function openExternal (url) {
  if (!url) return;
  if (isNative) {
    const Browser = plugin('Browser');
    if (Browser?.open) {
      try { await Browser.open({ url }); return; } catch { /* plugin not installed — fall through */ }
    }
  }
  window.open(url, '_blank', 'noopener');
}

// The label's words (tidy, displayName, specs, story) live in src/lib/label-text.js,
// shared with the native shelf's card and the printed back of the bag.
export { tidy, tidyAltitude, displayName } from '../lib/label-text.js';
</script>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonIcon } from '@ionic/vue';
import {
  chevronForward, openOutline, nutrition, flask, leaf, sunny, sparkles, flower,
} from 'ionicons/icons';
import { flavourById, roasterColour } from '../store.js';
import SpecGlossary from './SpecGlossary.vue';
import { displayName, labelNotes, labelSpecs, labelStory } from '../lib/label-text.js';

const props = defineProps({
  coffee: { type: Object, required: true },
  /** Off on the Coffee page, whose header bar already carries the name. */
  showName: { type: Boolean, default: true },
});
const emit = defineEmits(['navigate']);

const router = useRouter();
const uid = Math.random().toString(36).slice(2, 8);
const nameId = `cl-name-${uid}`;
const notesId = `cl-notes-${uid}`;
const storyId = `cl-story-${uid}`;
const storyBodyId = `cl-storybody-${uid}`;

const ICONS = { nutrition, flask, leaf, sunny, sparkles, flower };

const flavour = computed(() => flavourById[props.coffee.flavour] || null);
const flavourIcon = computed(() => ICONS[flavour.value?.icon] || leaf);
const colour = computed(() => roasterColour(props.coffee.roasterSlug));
const roasterPath = computed(() => `/tabs/shelf/roaster/${props.coffee.roasterSlug}`);

const notes = computed(() => labelNotes(props.coffee));
const specs = computed(() => labelSpecs(props.coffee));
const story = computed(() => labelStory(props.coffee));
const expanded = ref(false);
// Collapsed, it shows three lines, and never ends on a bare sub-heading.
const shownStory = computed(() => {
  if (expanded.value) return story.value;
  const out = story.value.slice(0, 3);
  while (out.length > 1 && out[out.length - 1].notice) out.pop();
  return out;
});

watch(() => props.coffee.slug, () => { expanded.value = false; });

function goRoaster () {
  emit('navigate', roasterPath.value);
  router.push(roasterPath.value);
}
</script>

<style scoped>
.label {
  --mono: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  color: var(--ink, #1F2A22);
}

/* The roaster, as the comp's kicker: small, spaced capitals, a link. */
.roaster {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  min-height: var(--tap);
  margin: calc(var(--sp-3) * -1) 0 calc(var(--sp-4) * -1);
  color: var(--muted, #5E6B60);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
}
.roaster:hover span:not(.roaster__dot) { color: var(--ink, #1F2A22); text-decoration: underline; text-underline-offset: 0.25em; }
.roaster:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; border-radius: var(--radius-sm); }
.roaster__dot { width: var(--sp-2); height: var(--sp-2); border-radius: 50%; background: var(--dot); box-shadow: inset 0 0 0 1px rgba(31, 42, 34, 0.18); }
.roaster__chev { color: var(--faint, #8A948B); font-size: var(--text-sm); }

.name {
  margin: 0;
  font-family: var(--font-display, 'Bricolage Grotesque', 'Manrope', system-ui, sans-serif);
  font-size: calc(var(--text-display) * 0.9);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.03em;
  text-wrap: balance;
}

.tags { display: flex; flex-wrap: wrap; gap: var(--sp-2); }
.chip {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  min-height: calc(var(--sp-6) - var(--sp-1));
  padding: 0 var(--sp-3);
  border-radius: 999px;
  background: var(--sage, #E6EDE4);
  color: var(--ink-2, #3E4A41);
  font-size: var(--text-sm);
  font-weight: 700;
}
.chip__icon { color: var(--fl); font-size: var(--text-md); }

.eyebrow {
  margin: 0 0 var(--sp-2);
  color: var(--muted, #5E6B60);
  font-family: var(--mono);
  font-size: calc(var(--text-xs) * 0.9);
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.notes__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0 var(--sp-3);
  margin: 0;
  padding: 0;
  list-style: none;
}
.notes__list li {
  font-family: var(--font-display, 'Bricolage Grotesque', 'Manrope', system-ui, sans-serif);
  font-size: var(--text-xl);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.015em;
  color: var(--leaf, #3F7A4E);
}
.notes__list li:not(:last-child)::after {
  content: '·';
  margin-left: var(--sp-3);
  color: var(--mustard, #F2C230);
  font-weight: 800;
}

.specs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4) var(--sp-4);
  margin: 0;
  padding: var(--sp-4) 0;
  border-block: 1px solid var(--line, #ECE6DC);
}
.spec { min-width: 0; }
.spec--wide { grid-column: 1 / -1; }
.spec dt { margin: 0; }
.spec dd {
  margin: var(--sp-1) 0 0;
  color: var(--ink, #1F2A22);
  font-size: calc(var(--text-md) * 0.9375);
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.story__title {
  margin: 0 0 var(--sp-2);
  font-family: var(--font-display, 'Bricolage Grotesque', 'Manrope', system-ui, sans-serif);
  font-size: var(--text-xl);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--ink, #1F2A22);
}
.story__body p {
  margin: 0 0 var(--sp-3);
  max-width: 38rem;
  color: var(--ink-2, #3E4A41);
  font-size: calc(var(--text-md) * 0.9375);
  line-height: 1.6;
  overflow-wrap: break-word;
}
.story__body p:last-child { margin-bottom: 0; }
.story__body .story__fact { margin-bottom: var(--sp-1); font-size: var(--text-sm); }
.story__key { font-weight: 700; color: var(--ink, #1F2A22); }
.story__key::after { content: ':'; }
/* A short shouted line: a sub-heading in the roaster's copy, or a notice. */
.story__body .story__notice {
  margin: var(--sp-4) 0 var(--sp-1);
  color: var(--muted, #5E6B60);
  text-transform: uppercase;
  font-family: var(--mono);
  font-size: calc(var(--text-xs) * 0.9);
  font-weight: 500;
  letter-spacing: 0.02em;
}

.textlink {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  min-height: var(--tap);
  padding: 0;
  border: 0;
  background: none;
  color: var(--ink, #1F2A22);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: var(--mustard, #F2C230);
  text-decoration-thickness: 2px;
  text-underline-offset: 0.3em;
  cursor: pointer;
}
.textlink:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; border-radius: var(--radius-sm); }
.story .textlink { margin-top: var(--sp-1); }
.textlink--out { align-self: flex-start; color: var(--muted, #5E6B60); font-weight: 600; text-decoration-color: var(--line, #ECE6DC); }
</style>
